import { type DetachedSurfaceInfo, serializeDetachHandle } from '@hibro/shared';

/**
 * Which inline copies a detached set hides (架构方案 §5.8 通用 Detachable).
 *
 * Keyed by the serialized {@link DetachHandle} rather than the bare `viewId`,
 * because an editor view can have several instance tabs open at once (§5.7 视图
 * 多实例) and detaching one of them must not blank the others. A whole-view
 * detach of a singleton keeps the historical key shape (`instanceKey` absent),
 * so existing rail-triggered detaches compare equal as before.
 */
export function detachedSurfaceKey(
  viewId: string,
  instanceKey?: string,
): string {
  return serializeDetachHandle({
    viewId,
    ...(instanceKey ? { instanceKey } : {}),
  });
}

/**
 * The keys whose inline copy must give way to a detached window: whole-view
 * (`fragmentId` absent) `migrate` entries only. A fragment window leaves its
 * origin untouched, and `mirror` is a copy rather than a move.
 */
export function migratedSurfaceKeys(
  surfaces: readonly DetachedSurfaceInfo[],
): ReadonlySet<string> {
  return new Set(
    surfaces
      .filter((surface) => !surface.fragmentId && surface.mode === 'migrate')
      .map((surface) =>
        detachedSurfaceKey(surface.viewId, surface.instanceKey),
      ),
  );
}

/** Whether this exact view instance is currently migrated into its own window. */
export function isDetachedInstance(
  keys: ReadonlySet<string> | undefined,
  viewId: string,
  instanceKey?: string,
): boolean {
  return keys?.has(detachedSurfaceKey(viewId, instanceKey)) ?? false;
}
